import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { ResourceType } from 'types/resource-types';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export type DeleteViewVariables = {
  viewId: string;
  resourceType: ResourceType;
};

export async function deleteViewFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteViewVariables,
): Promise<ApiResponse> {
  const { viewId } = variables;
  const instance = await axiosInstance;
  return instance.delete(`views/${viewId}`);
}

export function useDeleteView() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteViewVariables>({
    mutationFn: variables => deleteViewFn(axiosInstance, variables),
    onSuccess: (_response, { resourceType }) => {
      client.invalidateQueries({
        queryKey: ['views', resourceType],
      });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, DeleteViewVariables, 'deleteView'>(
    'deleteView',
    mutation,
  );
}
